import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useFetch, Card, Loader, PageHeader, Button, Field, Modal } from "../ui";

const tone = (ts) => ts === "OVERDUE" ? "bg-red-500/15 text-red-400" : ts === "DUE SOON" ? "bg-amber-500/15 text-amber-400" : "bg-[#C6FF00]/10 text-[#C6FF00]";
const remaining = (dl) => {
  if (!dl) return "";
  const ms = new Date(dl) - Date.now();
  if (ms < 0) return "OVERDUE";
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return `${h}H ${m}M`;
};

const Metric = ({ label, value, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <p className="text-2xl font-extrabold text-white">{value}</p>
    <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-[#8A939B]">{label}</p>
  </Card>
);

export default function Acquisition() {
  const navigate = useNavigate();
  const { data: dash, loading: l1 } = useFetch("/admin/acquisition/dashboard", []);
  const { data: q, loading: l2, reload } = useFetch("/admin/acquisition/queue", []);
  const { data: slotsData, reload: reloadSlots } = useFetch("/admin/acquisition/slots", []);
  const { data: pulse } = useFetch("/admin/acquisition/pulse", []);
  const { data: strat, reload: reloadStrat } = useFetch("/admin/acquisition/strategy-calls", []);
  const { data: ns, reload: reloadNs } = useFetch("/admin/acquisition/no-shows", []);
  const { data: gcal, reload: reloadGcal } = useFetch("/admin/gcal/status", []);
  const [tab, setTab] = useState("queue");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [slotDate, setSlotDate] = useState("");
  const [slotTimes, setSlotTimes] = useState("10:00, 13:00, 15:30");
  const [selCall, setSelCall] = useState(null);
  const [callBrief, setCallBrief] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("gcal") === "connected") { toast.success("Google Calendar connected — bookings now sync automatically"); window.history.replaceState({}, "", window.location.pathname); }
    if (p.get("gcal") === "error") { toast.error("Google Calendar connection failed — try again"); window.history.replaceState({}, "", window.location.pathname); }
    if (p.get("gcal") === "noscope") { toast.error("Calendar permission was not granted — reconnect and CHECK the \"See, edit, share your calendars\" box on Google's screen", { duration: 12000 }); window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  if (l1 || l2 || !dash || !q) return <Loader />;
  const m = dash.metrics;
  const maxF = Math.max(...dash.funnel.map((f) => f.count), 1);

  const addSlots = async () => {
    if (!slotDate) return toast.error("Pick a date");
    const slots = slotTimes.split(",").map((t) => t.trim()).filter(Boolean).map((t) => new Date(`${slotDate}T${t}:00`).toISOString());
    try {
      const r = await api.post("/admin/acquisition/slots", { slots, duration_min: 30 });
      toast.success(`Added ${r.data.added} slot(s)`);
      reloadSlots();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delLead = async (e, l) => {
    e.stopPropagation();
    if (!window.confirm(`Delete lead ${l.first_name} ${l.last_name} (${l.business_name})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/acquisition/leads/${l.id}`); toast.success("Lead deleted"); reload(); } catch (err) { toast.error(apiError(err)); }
  };
  const resetAll = async () => {
    if (!window.confirm("Reset ALL acquisition data? Every lead, event and metric will be wiped back to zero. This cannot be undone.")) return;
    try { const r = await api.post("/admin/acquisition/reset"); toast.success(`Reset complete — ${r.data.deleted_leads} lead(s) removed`); window.location.reload(); } catch (err) { toast.error(apiError(err)); }
  };

  const connectGcal = async () => {
    try { const r = await api.get("/admin/gcal/login"); window.location.href = r.data.authorization_url; } catch (err) { toast.error(apiError(err)); }
  };
  const disconnectGcal = async () => {
    if (!window.confirm("Disconnect Google Calendar? New bookings will stop syncing.")) return;
    try { await api.post("/admin/gcal/disconnect"); toast.success("Google Calendar disconnected"); reloadGcal(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Client Acquisition OS" title="Acquisition" subtitle="Brand Score submissions, audit queue and funnel — the HughLaunch client acquisition system."
        actions={<div className="flex flex-wrap gap-2">
          {gcal?.connected ? (
            <Button variant="ghost" onClick={disconnectGcal} data-testid="gcal-disconnect-btn" title={`Connected as ${gcal.email}`}>
              <span className="text-[#C6FF00]">●</span> Google Calendar Synced
            </Button>
          ) : (
            <Button variant="ghost" onClick={connectGcal} data-testid="gcal-connect-btn">Connect Google Calendar</Button>
          )}
          <Button variant="ghost" onClick={resetAll} data-testid="acq-reset-btn">Reset All To Zero</Button>
        </div>} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Metric label="New Submissions Today" value={m.new_submissions} testid="acq-m-new" />
        <Metric label="Awaiting Call Booking" value={m.awaiting_booking} testid="acq-m-awaiting" />
        <Metric label="Confirmed Audits" value={m.confirmed_audits} testid="acq-m-confirmed" />
        <Metric label="Audits To Review" value={m.audits_to_review} testid="acq-m-review" />
        <Metric label="Audits Due Today" value={m.audits_due_today} testid="acq-m-due-today" />
        <Metric label="Audits Overdue" value={m.audits_overdue} testid="acq-m-overdue" />
        <Metric label="Review Calls Today" value={m.calls_today} testid="acq-m-calls" />
        <Metric label="Prescriptions Sent" value={m.prescriptions_sent} testid="acq-m-rx" />
        <Metric label="Clients Won" value={m.clients_won} testid="acq-m-won" />
        <Metric label="Revenue Won" value={`$${Number(m.revenue_won).toLocaleString()}`} testid="acq-m-revenue" />
      </div>

      {pulse && (
        <Card className="p-6" data-testid="acq-pulse">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">Acquisition Pulse</p>
              <p className="mt-1 text-4xl font-extrabold text-[#C6FF00]">{pulse.pulse}<span className="text-lg text-[#8A939B]">/100</span></p>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
              {[["STRONGEST POINT", pulse.strongest], ["BIGGEST LEAK", pulse.biggest_leak], ["NEXT RECOMMENDED ACTION", pulse.next_action]].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-[#14161b] p-3"><p className="text-[10px] font-bold uppercase text-[#8A939B]">{k}</p><p className="mt-1 text-xs font-semibold text-white">{v}</p></div>
              ))}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {pulse.components.map((c) => (
              <div key={c.label} className="rounded-lg bg-[#14161b] p-2 text-center"><p className="text-sm font-extrabold text-white">{c.score}</p><p className="text-[9px] font-bold uppercase text-[#8A939B]">{c.label}</p></div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6" data-testid="acq-funnel">
        <p className="mb-4 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Acquisition Funnel</p>
        <div className="space-y-2">
          {dash.funnel.map((f) => (
            <div key={f.label} className="flex items-center gap-3">
              <span className="w-52 shrink-0 text-[11px] font-bold uppercase tracking-wide text-[#8A939B]">{f.label}</span>
              <div className="h-6 flex-1 overflow-hidden rounded bg-[#14161b]">
                <div className="flex h-full items-center rounded bg-[#C6FF00]/80 px-2 text-xs font-extrabold text-[#0A0C0F]" style={{ width: `${Math.max(4, (f.count / maxF) * 100)}%` }}>{f.count}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex gap-2">
        {[["queue", `Audit Queue (${q.queue.length})`], ["leads", `All Leads (${q.leads.length})`], ["strategy", `Strategy Calls (${strat?.upcoming?.length ?? 0})`], ["slots", "Call Slots"]].map(([k, label]) => (
          <button key={k} data-testid={`acq-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === k ? "bg-[#C6FF00] text-[#0A0C0F]" : "bg-[#14161b] text-[#8A939B]"}`}>{label}</button>
        ))}
      </div>

      {tab !== "slots" && tab !== "strategy" && (
        <div className="space-y-2">
          {tab === "leads" && (
            <div className="flex flex-wrap gap-2 pb-2">
              <input data-testid="acq-search" placeholder="Search name, business, email, source…" value={search} onChange={(e) => setSearch(e.target.value)}
                className="flex-1 rounded-xl border border-[#23262c] bg-[#14161b] px-4 py-2.5 text-sm text-white outline-none focus:border-[#C6FF00]" />
              <select data-testid="acq-stage-filter" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                className="rounded-xl border border-[#23262c] bg-[#14161b] px-3 py-2.5 text-sm text-white">
                <option value="">All stages</option>
                {["FORM COMPLETED — BOOKING REQUIRED", "AUDIT CONFIRMED", "AUDIT READY", "AUDIT REVIEW COMPLETED", "PRESCRIPTION SENT", "CLIENT — WON", "NURTURE"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          {(tab === "queue" ? q.queue : q.leads.filter((l) => {
            const t = `${l.first_name} ${l.last_name} ${l.business_name} ${l.email} ${l.lead_source}`.toLowerCase();
            return (!search || t.includes(search.toLowerCase())) && (!stageFilter || l.stage === stageFilter);
          })).map((l) => (
            <div key={l.id} data-testid={`acq-lead-${l.id}`} onClick={() => navigate(`/admin/acquisition/${l.id}`)} role="button" tabIndex={0}
              className="flex w-full cursor-pointer flex-wrap items-center gap-3 rounded-2xl border border-[#1c1f25] bg-[#101216] p-4 text-left hover:border-[#3a3f47]">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-white">{l.first_name} {l.last_name} <span className="text-[#8A939B]">· {l.business_name}</span></p>
                <p className="mt-0.5 text-xs text-[#8A939B]">{l.stage} · src: {l.lead_source}{l.call ? ` · Call: ${l.call.start_display}` : ""}</p>
              </div>
              <span className="rounded-full bg-[#14161b] px-2.5 py-1 text-xs font-bold text-white">Score {(l.final_scores || l.preliminary_scores)?.overall}</span>
              <span className="rounded-full bg-[#14161b] px-2.5 py-1 text-xs font-bold text-[#8A939B]">Opp {l.opportunity_score} · {l.opportunity_class}</span>
              {l.last_nurture && <span data-testid={`acq-nurture-${l.id}`} className="rounded-full bg-sky-500/10 px-2.5 py-1 text-xs font-bold text-sky-400">✉ {l.last_nurture.label} · {new Date(l.last_nurture.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
              {l.time_status && <span className={`rounded-full px-2.5 py-1 text-xs font-extrabold ${tone(l.time_status)}`}>{l.time_status === "OVERDUE" ? "OVERDUE" : `AUDIT DUE IN ${remaining(l.audit_deadline)}`}</span>}
              <button data-testid={`acq-del-lead-${l.id}`} onClick={(e) => delLead(e, l)} title="Delete lead"
                className="rounded-full p-2 text-[#8A939B] transition-colors hover:bg-red-500/10 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {(tab === "queue" ? q.queue : q.leads).length === 0 && <Card className="p-8 text-center text-sm text-[#8A939B]">Nothing here yet.</Card>}
        </div>
      )}

      {tab === "strategy" && (
        <div className="space-y-6" data-testid="acq-strategy-calls">
          {[["Upcoming", strat?.upcoming || []], ["Past", strat?.past || []]].map(([label, calls]) => (
            <div key={label}>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">{label} ({calls.length})</p>
              {calls.length === 0 && <Card className="p-6 text-center text-sm text-[#8A939B]">No {label.toLowerCase()} strategy calls.</Card>}
              <div className="space-y-2">
                {calls.map((c) => (
                  <div key={c.id} data-testid={`strategy-call-${c.id}`} onClick={() => setSelCall(c)} role="button" tabIndex={0}
                    className="cursor-pointer rounded-2xl border border-[#1c1f25] bg-[#101216] p-4 transition-colors hover:border-[#3a3f47]">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-white">{c.name} {c.business && <span className="text-[#8A939B]">· {c.business}</span>}</p>
                        <p className="mt-0.5 text-xs text-[#8A939B]">{c.email} · booked {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
                      </div>
                      <span className="rounded-full bg-[#C6FF00]/10 px-3 py-1 text-xs font-extrabold text-[#C6FF00]">{c.start_display}</span>
                      {c.no_show && <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-extrabold text-red-400">NO-SHOW</span>}
                      {!c.no_show && (
                        <button data-testid={`noshow-strategy-${c.id}`} title="Mark as no-show — permanently blocks this email from booking"
                          onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Mark ${c.name} as a no-show? ${c.email} will be permanently blocked from booking again.`)) return; try { await api.post(`/admin/acquisition/strategy-calls/${c.id}/no-show`); toast.success("Marked no-show — email blocked from booking"); reloadStrat(); reloadNs(); } catch (err) { toast.error(apiError(err)); } }}
                          className="rounded-full border border-red-500/40 px-3 py-1 text-xs font-bold text-red-400 transition-colors hover:bg-red-500/10">
                          No-Show
                        </button>
                      )}
                      {label === "Upcoming" && (
                        <button data-testid={`cancel-strategy-${c.id}`} title="Cancel call (frees the slot)"
                          onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Cancel strategy call with ${c.name}? The slot becomes available again.`)) return; try { await api.delete(`/admin/acquisition/strategy-calls/${c.id}`); toast.success("Call cancelled — slot freed"); reloadStrat(); reloadSlots(); } catch (err) { toast.error(apiError(err)); } }}
                          className="rounded-full p-2 text-[#8A939B] transition-colors hover:bg-red-500/10 hover:text-red-400">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {c.notes && <p className="mt-3 rounded-xl bg-[#14161b] p-3 text-sm text-[#c8cdd3]"><span className="font-bold text-[#8A939B]">Wants to cover:</span> {c.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div data-testid="acq-noshow-list">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Blocked — No-Shows ({(ns?.no_shows || []).length})</p>
            {(ns?.no_shows || []).length === 0 ? (
              <Card className="p-4 text-sm text-[#8A939B]">No blocked emails. Mark a past call as no-show to block that email from booking.</Card>
            ) : (
              <div className="space-y-2">
                {ns.no_shows.map((b) => (
                  <div key={b.email} data-testid={`noshow-row-${b.email}`} className="flex items-center justify-between rounded-xl border border-red-500/20 bg-[#101216] px-4 py-3 text-sm">
                    <span className="text-white">{b.name || "—"} <span className="text-[#8A939B]">· {b.email} · {b.source} call · {new Date(b.at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span></span>
                    <button data-testid={`unblock-${b.email}`}
                      onClick={async () => { if (!window.confirm(`Unblock ${b.email}? They'll be able to book calls again.`)) return; try { await api.delete(`/admin/acquisition/no-shows/${encodeURIComponent(b.email)}`); toast.success("Unblocked"); reloadNs(); } catch (err) { toast.error(apiError(err)); } }}
                      className="text-xs font-bold text-[#C6FF00]">Unblock</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal open={!!selCall} onClose={() => { setSelCall(null); setCallBrief(null); }} title="Strategy Call Details" testid="strategy-call-modal">
        {selCall && (
          <div className="space-y-3">
            {[["Name", selCall.name], ["Email", selCall.email], ["Business", selCall.business || "—"],
              ["Call time", selCall.start_display], ["Duration", `${selCall.duration_min} minutes`],
              ["Booked on", new Date(selCall.created_at).toLocaleString("en-US", { month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })],
              ["Morning reminder", selCall.morning_reminder_sent ? "Sent" : "Not yet sent"]].map(([k, v]) => (
              <p key={k} className="text-sm"><span className="font-bold text-[#8A939B]">{k}:</span> <span className="text-white">{v}</span></p>
            ))}
            <div className="rounded-xl bg-[#14161b] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#8A939B]">What they want to cover</p>
              <p className="mt-2 text-sm text-[#c8cdd3]" data-testid="strategy-modal-notes">{selCall.notes || "Nothing specified."}</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="ghost" data-testid="strategy-call-brief-btn" onClick={async () => { try { const r = await api.get(`/admin/delivery/brief/strategy/${selCall.id}`); setCallBrief(r.data.brief); } catch (err) { toast.error(apiError(err)); } }}>Meeting Brief</Button>
              <Button variant="ghost" as="a" href={`mailto:${selCall.email}`} data-testid="strategy-modal-email">Email {selCall.name.split(" ")[0]}</Button>
              <Button variant="ghost" onClick={() => setSelCall(null)}>Close</Button>
            </div>
            {callBrief && (
              <div className="mt-3 rounded-xl bg-[#14161b] p-4 text-sm" data-testid="strategy-brief">
                <p className="text-xs font-bold uppercase tracking-wider text-[#C6FF00]">Meeting Brief {callBrief.is_client ? "· EXISTING CLIENT" : "· PROSPECT"}</p>
                {callBrief.is_client ? (
                  <div className="mt-2 space-y-1 text-[#c8cdd3]">
                    <p><span className="font-bold text-[#8A939B]">Stage:</span> {callBrief.current_stage} · {callBrief.progress}%</p>
                    <p><span className="font-bold text-[#8A939B]">Next milestone:</span> {callBrief.next_milestone}</p>
                    {callBrief.blockers?.length > 0 && <p className="text-red-400">Blockers: {callBrief.blockers.join(", ")}</p>}
                    {callBrief.decisions_required?.length > 0 && <p className="text-purple-400">Decisions: {callBrief.decisions_required.join("; ")}</p>}
                    {callBrief.client_questions?.length > 0 && <p><span className="font-bold text-[#8A939B]">Open approvals:</span> {callBrief.client_questions.join("; ")}</p>}
                    {callBrief.outstanding_items?.length > 0 && <p><span className="font-bold text-[#8A939B]">Outstanding:</span> {callBrief.outstanding_items.slice(0, 5).join(", ")}</p>}
                    {callBrief.completed_work?.length > 0 && <p><span className="font-bold text-[#8A939B]">Done:</span> {callBrief.completed_work.slice(0, 5).join(", ")}</p>}
                  </div>
                ) : (
                  <div className="mt-2 space-y-1 text-[#c8cdd3]">
                    <p><span className="font-bold text-[#8A939B]">Wants to cover:</span> {callBrief.wants_to_cover || "—"}</p>
                    {callBrief.brand_score != null && <p><span className="font-bold text-[#8A939B]">Brand Score:</span> {callBrief.brand_score}/100</p>}
                    <p className="text-[#8A939B]">New prospect — no client record yet. Lead with diagnosis, close with the Brand Score.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {tab === "slots" && (
        <Card className="space-y-5 p-6" data-testid="acq-slots-manager">
          <p className="text-xs text-[#8A939B]">Mon / Wed / Fri slots (9am–5pm PT, every 2 hours) are auto-generated one week ahead. Add extra one-off times below if needed.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Date" type="date" value={slotDate} onChange={(e) => setSlotDate(e.target.value)} data-testid="acq-slot-date" />
            <Field label="Times (comma separated, 24h)" value={slotTimes} onChange={(e) => setSlotTimes(e.target.value)} data-testid="acq-slot-times" />
            <div className="flex items-end"><Button onClick={addSlots} data-testid="acq-add-slots-btn">Add Slots</Button></div>
          </div>
          <div className="space-y-2">
            {(slotsData?.slots || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-xl border border-[#1c1f25] bg-[#14161b] px-4 py-3 text-sm">
                <span className="font-semibold text-white">{new Date(s.start).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                {s.booked_by ? <span className="text-xs font-bold text-[#C6FF00]">BOOKED</span> : (
                  <button data-testid={`acq-del-slot-${s.id}`} onClick={async () => { await api.delete(`/admin/acquisition/slots/${s.id}`); reloadSlots(); }} className="text-xs font-bold text-red-400">Remove</button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
